import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { Credentials, CredentialsService, throwOnGraphqlError } from '@app/core';
import { AuthenticationMethod, Purpose } from '@app/models';
import {
  AuthenticationResult,
  CompleteRegistrationGQL,
  CompleteRegistrationMutationVariables,
  LoginEmailGQL,
  LoginEmailMutationVariables,
  LoginSignatureGQL,
  LoginSignatureMutationVariables,
  SendEmailVerificationPromptGQL,
  SendEmailVerificationPromptMutationVariables,
  UserExistGQL,
  UserExistQueryVariables,
  VerifyBiometricAuthGQL,
  VerifyBiometricAuthMutationVariables,
  VerifyEmailGQL,
  VerifyEmailMutationVariables
} from '../../generated/graphql.auth';

// tslint:disable-next-line:no-empty-interface
export interface BaseLoginContext {}
export interface LoginContext extends BaseLoginContext {
  email: string;
  password: string;
  referral?: string;
}
export interface RegistrationContext {
  name: string;
  email: string;
  cpf: string;
  birthday: string;
  password: string;
  password_confirmation?: string;
  company?: string;
  cnpj?: string;
  referral?: string;
  country: string;
  language: string;
  timezone?: string;
  verify_token?: string;
}
export interface ResetPasswordContext {
  email: string;
  token: string;
  password: string;
  password_confirmation: string;
}

/**
 * Provides a base for authentication workflow.
 * The auth-login/logout methods should be replaced with proper implementation.
 */
@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  static parseCredentials(responseBody: any, method: AuthenticationMethod = AuthenticationMethod.Email): Credentials {
    return {
      accessToken: responseBody.access_token,
      tokenType: responseBody.token_type,
      expiresAt: new Date().setSeconds(new Date().getSeconds() + responseBody.expires_in),
      firebaseToken: responseBody.firebase_jwt,
      mfa: responseBody.mfa || [],
      mfaAccepted: method === AuthenticationMethod.Enterprise
    };
  }

  constructor(
    private credentialsService: CredentialsService,
    private httpClient: HttpClient,
    private userExistGQL: UserExistGQL,
    private loginEmailGQL: LoginEmailGQL,
    private sendEmailVerificationPromptGQL: SendEmailVerificationPromptGQL,
    private completeRegistrationGQL: CompleteRegistrationGQL,
    private loginSignatureGQL: LoginSignatureGQL,
    private verifyEmailGQL: VerifyEmailGQL,
    private verifyBiometricAuthGQL: VerifyBiometricAuthGQL
  ) {}

  /**
   * Authenticates the user.
   * @param context The login parameters.
   * @param method Authentication method. Defaults to the enum key 'email'.
   * @return The user credentials.
   */
  login(context: BaseLoginContext, method: AuthenticationMethod = AuthenticationMethod.Email): Observable<Credentials> {
    if ((context as LoginContext).email) {
      (context as LoginContext).email = ((context as LoginContext).email || '').trim();
    }

    return this.httpClient.post('/auth/login/' + method, context).pipe(
      map(body => AuthenticationService.parseCredentials(body, method)),
      tap(credentials => this.credentialsService.setCredentials(credentials))
    );
  }

  /**
   * Logs out the user and clears credentials.
   */
  logout() {
    let logout$;

    if (this.isAuthenticated()) {
      logout$ = this.httpClient.disableLoader().post('/auth/logout', null, { headers: this.credentialsService.authorizationHeader });
    }
    this.credentialsService.clearCredentials();

    return logout$ || of(null);
  }

  /**
   * Signs up the user.
   * @return The user credentials.
   */
  register(context: RegistrationContext): Observable<Credentials & { isEmailConfirmed: boolean }> {
    context.password_confirmation = context.password;
    context.email = (context.email || '').trim().toLowerCase();
    context.name = (context.name || '').trim();
    context.company = (context.company || '').trim() || null;
    context.cnpj = (context.cnpj || '').trim() || null;

    if (context.name && context.name === context.name.toUpperCase()) {
      context.name = context.name
        .toLowerCase()
        .split(' ')
        .filter(part => !!part)
        .map(part => part[0].toUpperCase() + part.substring(1))
        .join(' ');
    }

    return this.httpClient.post('/auth/register/email', context).pipe(
      map((body: any) => {
        const credentials = AuthenticationService.parseCredentials(body);
        if (body.verified) {
          this.credentialsService.setCredentials(credentials);
        }
        return { isEmailConfirmed: body.verified, ...credentials };
      })
    );
  }

  /**
   * Sends email with link to change password.
   * @param email The user's email.
   */
  sendResetPasswordConfirmation(email: string) {
    return this.httpClient.post('/password/email', { email: (email || '').trim() });
  }

  /**
   * Sends email with link to change password.
   * @param context The user's token and new password.
   */
  resetPassword(context: ResetPasswordContext) {
    context.password_confirmation = context.password;
    return this.httpClient.post('/password/reset', context);
  }

  /**
   * Checks if the user is authenticated.
   * @return True if the user is authenticated.
   */
  isAuthenticated(): boolean {
    return this.credentialsService.isAuthenticated();
  }

  /**
   * Checks if the user is authenticated, but did not accept 2FA yet.
   * @return True if the conditions are met
   */
  isMissingTwoStep(): boolean {
    return this.credentialsService.isMissingTwoStep();
  }

  clearAllFields() {
    document.querySelectorAll('.tokenField__field').forEach((e: any) => {
      e.value = '';
    });
  }

  userExists(attributes: UserExistQueryVariables) {
    if (attributes.phone) {
      attributes.phone = (attributes.phone || '').trim();
    }
    if (attributes.email) {
      attributes.email = (attributes.email || '').trim();
    }
    return this.userExistGQL.fetch(attributes, { fetchPolicy: 'no-cache' }).pipe(
      throwOnGraphqlError(),
      map(response => response.data.userExist)
    );
  }

  sendEmailVerificationPrompt(variables: SendEmailVerificationPromptMutationVariables) {
    return this.sendEmailVerificationPromptGQL.mutate(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.sendEmailVerificationPrompt)
    );
  }

  verifyEmail(variables: VerifyEmailMutationVariables) {
    return this.verifyEmailGQL.mutate(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.verifyEmail)
    );
  }

  loginEmail(variables: LoginEmailMutationVariables) {
    return this.loginEmailGQL.mutate(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.loginEmail),
      tap(credentials => this.setCredentials(credentials))
    );
  }

  loginSignature(variables: LoginSignatureMutationVariables) {
    return this.loginSignatureGQL.mutate(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.loginSignature),
      tap(credentials => this.setCredentials(credentials))
    );
  }

  completeRegistration(variables: CompleteRegistrationMutationVariables) {
    return this.completeRegistrationGQL.mutate(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.completeRegistration),
      tap(credentials => this.setCredentials(credentials))
    );
  }

  setCredentials(credentials: AuthenticationResult, purpose?: Purpose) {
    this.credentialsService.setCredentials({
      accessToken: credentials.access_token,
      tokenType: credentials.token_type,
      expiresAt: new Date().setSeconds(new Date().getSeconds() + credentials.expires_in),
      mfa: credentials.mfa || [],
      purpose
    });
  }

  verifyBiometricAuth(variables: VerifyBiometricAuthMutationVariables) {
    return this.verifyBiometricAuthGQL.mutate(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.verifyBiometricAuth),
      map(result => {
        switch (result.__typename) {
          case 'AuthenticationResult':
            return { authentication: result, verification: null };

          case 'SecurityVerificationType':
            return { authentication: null, verification: result };

          case 'VerifyBiometricAuthCombinedType':
            return {
              authentication: result.authentication ?? null,
              verification: result.security_verification ?? null
            };

          default:
            return { authentication: null, verification: null };
        }
      }),
      tap(({ authentication }) => {
        if (authentication?.access_token) {
          this.setCredentials(authentication, Purpose.Signing);
        }
      })
    );
  }
}
